import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { RoleLayout } from "@/app/layouts/RoleLayout";
import { SettingsPage } from "@/features/account/SettingsPage";
import { AcceptInvitePage } from "@/features/auth/AcceptInvitePage";
import { ForgotPasswordPage } from "@/features/auth/ForgotPasswordPage";
import { LoginPage } from "@/features/auth/LoginPage";
import { RequireAuth } from "@/features/auth/RequireAuth";
import { ResetPasswordPage } from "@/features/auth/ResetPasswordPage";
import { ClubsPage } from "@/features/clubs/ClubsPage";
import { ClubDetailPage } from "@/features/clubs/ClubDetailPage";
import { ClubEditPage } from "@/features/clubs/ClubEditPage";
import { ClubCalendarPage, OpsCalendarPage } from "@/features/calendar/CalendarPage";
import { EmailLogPage } from "@/features/email-log/EmailLogPage";
import { OpsDashboard } from "@/features/ops/OpsDashboard";
import { OpsTripsPage } from "@/features/ops/OpsTripsPage";
import { RequestsPage } from "@/features/ops/RequestsPage";
import { ClubMessagesPage, OpsMessagesPage } from "@/features/messaging/InboxPage";
import { FacturaPage } from "@/features/records/FacturaPage";
import { ClubInvoicesPage, OpsInvoicesPage } from "@/features/records/InvoicesPage";
import { RosterPage } from "@/features/roster/RosterPage";
import { RequestPage } from "@/features/trips/RequestPage";
import { TripDetailPage } from "@/features/trips/TripDetailPage";
import { TripsPage } from "@/features/trips/TripsPage";
import { AuthzProvider, useAuthz } from "@/lib/auth/authz";
import { Dashboard } from "@/pages/Dashboard";
import { KitPage } from "@/pages/KitPage";
import { Placeholder } from "@/pages/Placeholder";

/**
 * Render one of two surfaces by audience. Several routes (the landing dashboard, the trips
 * list) have a distinct back-office (cross-club) and club (single-tenant) view; this picks the
 * right one from `useAuthz().isBackOffice`, the same signal RoleLayout uses to choose the shell.
 */
function AudienceRoute({ backOffice, club }: { backOffice: ReactNode; club: ReactNode }) {
  const { isBackOffice } = useAuthz();
  return <>{isBackOffice ? backOffice : club}</>;
}

// Sentry-instrumented <Routes>, so performance transactions are tagged with the matched route
// pattern (e.g. "/trips/:id") rather than the raw URL. No-op when Sentry is disabled (dev).
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

export function App() {
  return (
    <BrowserRouter>
      <SentryRoutes>
        {/* Unauthenticated views: login, invite set-password, and forgot/reset password. */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

        {/* The component kit is a full-bleed living style guide (its own chrome), so it sits
            beside the shells rather than inside one. Authed, but no role shell. */}
        <Route
          path="/kit"
          element={
            <RequireAuth>
              <KitPage />
            </RequireAuth>
          }
        />

        {/* Authed product surfaces live inside a role-based shell (Back Office vs Club).
            RoleLayout mounts the right shell per user type and renders the matched child. */}
        <Route
          element={
            <RequireAuth>
              <AuthzProvider>
                <RoleLayout />
              </AuthzProvider>
            </RequireAuth>
          }
        >
          <Route index element={<AudienceRoute backOffice={<OpsDashboard />} club={<Dashboard />} />} />
          <Route
            path="/trips"
            element={<AudienceRoute backOffice={<OpsTripsPage />} club={<TripsPage />} />}
          />
          <Route path="/trips/:id" element={<TripDetailPage />} />
          <Route path="/trips/:id/invoices/:invoiceId" element={<FacturaPage />} />
          <Route path="/trips/:id/edit" element={<RequestPage />} />
          <Route path="/request" element={<RequestPage />} />
          <Route
            path="/requests"
            element={
              <AudienceRoute
                backOffice={<RequestsPage />}
                club={<Placeholder titleKey="nav.requests" icon="bell" />}
              />
            }
          />
          <Route
            path="/calendar"
            element={<AudienceRoute backOffice={<OpsCalendarPage />} club={<ClubCalendarPage />} />}
          />
          <Route path="/roster" element={<RosterPage />} />
          <Route path="/clubs" element={<ClubsPage />} />
          <Route path="/clubs/:clubId" element={<ClubDetailPage />} />
          <Route path="/clubs/:clubId/edit" element={<ClubEditPage />} />
          <Route
            path="/messages"
            element={<AudienceRoute backOffice={<OpsMessagesPage />} club={<ClubMessagesPage />} />}
          />
          <Route
            path="/invoices"
            element={<AudienceRoute backOffice={<OpsInvoicesPage />} club={<ClubInvoicesPage />} />}
          />
          <Route
            path="/emails"
            element={
              <AudienceRoute
                backOffice={<EmailLogPage />}
                club={<Placeholder titleKey="emailLog.title" icon="mail" />}
              />
            }
          />
          <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
          <Route path="/settings/:section" element={<SettingsPage />} />
        </Route>
      </SentryRoutes>
    </BrowserRouter>
  );
}
